import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { makeStyles } from '@material-ui/core';
import { useSelector, useDispatch } from 'react-redux';
import UtilService from '../../../../../helper/service';
import { updateSettings } from "../../../../../store/actions/creator/analysis";
import VideoSection from './live/video';
import BackgroundLines from './completed/graph/bglines';
import PencilMarker from './completed/notes/pencilMarker';
import ReactCursorPosition from 'react-cursor-position';
import MarkerSection from './completed/notes/markerSection';
import AgendaSection from './completed/notes/agendaSection';
import CoreAnalysisBarGraph from './completed/graph/bar';
import PillSection from './completed/pills';
import CoreAnalysisLineBarGraph from './completed/graph/linebar';
import useWindowSize from '../../../../../helper/hooks/useWindowSize';
import ScorePane from './completed/graph/scorePane';
import clsx from 'clsx';
import ClipSection from './completed/clip';
import { enableHorizontalScroll } from '../../../../../helper/hooks/enableHorizontalScroll';
import {
  changeGraphType,
  getAgendaImmersions,
  getAgendaSafes
} from "../../../../../store/actions/creator/creatorExperienceManager";
import { useHistory } from 'react-router';
import { Fade } from "react-reveal";
import Draggable from "react-draggable";
import { ResizableBox } from 'react-resizable';

function CompletedSection() {

  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const scrollRef: any = useRef();
  const scrollAllPartRef: any = useRef();
  const pencilRef: any = useRef();

  const { currentSession, all, loading, identifiers, cliped, analyseStart, analyseEnd, stimulus, settings, markers } = useSelector((state: any) => state.analysis);
  const { pencil, graphZoom, clip, xScroll, videoPlayed, addNoteX, addNoteY, videoPopIn, pdfLoading, videoRemoved } = settings;
  const { session_code, session_status, session_type } = currentSession;
  const { graphType } = useSelector((state: any) => state.creatorExperienceManager);

  const [videoWidth, setVideoWidth] = useState(240);
  const [videoHeight, setVideoHeight] = useState(135);
  const [isDraggable, setIsDraggable] = useState(true);

  const loginUserData: any = localStorage.getItem("user_data") ? JSON.parse(localStorage.getItem("user_data") || "{}") : "";
  const { width } = useWindowSize();
  const paneWidth = (width && width > 1200) ? (width - (!clip ? 600 : 200)) : 600;

  const graphLength = useMemo(() => {
    if (cliped && stimulus) return (cliped.end_clip - cliped.start_clip) + 1;
    if (stimulus && session_type === 'ASYNC') return stimulus.stimulus_video_length;
    if (cliped) return (cliped.end_clip - cliped.start_clip) + 1;
    return analyseEnd - analyseStart
  }, [stimulus, cliped, analyseEnd, analyseStart]);

  const videoStart = cliped ? (cliped.start_clip - (session_type === 'ASYNC' ? 0 : analyseStart)) : 0;
  const videoLength = stimulus ? stimulus.stimulus_video_length : graphLength;

  const clipLength = cliped ? cliped.end_clip - (session_type === 'ASYNC' ? 0 : analyseStart) : 0;
  const videoEnd = cliped ? (clipLength > videoLength ? videoLength : clipLength) : videoLength;

  const getMaxWidth = Math.floor(graphLength / Math.pow(graphZoom, 2) * 15);
  const videoAvailable = (!cliped || cliped && (cliped.start_clip - analyseStart < stimulus?.stimulus_video_length)) && !videoRemoved
  const clipRatio = stimulus?.stimulus_video_length / graphLength
  const isbluelineWithoutStimulus = !!videoPopIn && (!videoPlayed || (!videoAvailable && cliped));

  const blueLineWidth = isbluelineWithoutStimulus
    ? ((videoPopIn - videoStart) * 15 / Math.pow(graphZoom, 2))
    : (videoPlayed - (videoStart / (stimulus ? stimulus.stimulus_video_length : graphLength))) * clipRatio * getMaxWidth || 0

  const blueLineLabel = isbluelineWithoutStimulus
    ? UtilService.getTimeLabel(videoPopIn, 1, 0)
    : UtilService.getTimeLabel(videoPlayed * (stimulus ? stimulus.stimulus_video_length : graphLength), 1, 0)

  const graphs = all
    .filter((item: any) =>
      // !excludedParticipants.includes(item.activeParticipant) &&
      (identifiers.includes(item.activeParticipant) || item.activeParticipant === 'All' || item.isTag)
    )
    .map((item: any) => (cliped ? {
      ...item, immersion: {
        ...item.immersion, data: item.immersion.data.filter((datum: any) => {
          return UtilService.getLocalEpoch2(datum.time_stamp) >= cliped.start_clip && UtilService.getLocalEpoch2(datum.time_stamp) <= cliped.end_clip
        })
      },
    } : item))
    .map((item: any) => (cliped ? {
      ...item, pills: item.pills.filter((datum: any) => {
        return (
          (cliped.start_clip <= datum.start && datum.start <= cliped.end_clip)
          || (cliped.start_clip <= datum.end && datum.end <= cliped.end_clip)
          || (datum.start <= cliped.start_clip && cliped.end_clip <= datum.end)
        )
      })
    } : item))
    .map((item: any) => (cliped ? {
      ...item, safety: item.safety.filter((datum: any) => {
        return datum.epoch_start + 120 >= cliped.start_clip && datum.epoch_start <= cliped.end_clip
      })
    } : item));

  const handleKeyPress = (event: any) => {
    if (event.keyCode === 37 || event.keyCode === 39) {
      event.preventDefault();
      event.keyCode === 39 ? onArrowScroll(30) : onArrowScroll(-30);
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  });

  useEffect(() => {
    if ((currentSession['session_status'] === 'COMPLETED' || (currentSession['session_type'] === 'ASYNC')) &&
      identifiers && identifiers.length > 0 &&
      markers && markers.length > 0) {
      let reqMarkers = markers.filter((item: any) => {
        return (item.marker_type === 1)
      });

      reqMarkers = reqMarkers.map((item: any) => {
        return { start: item.start_marker_at, end: item.end_marker_at };
      })

      const fetchAgendaData = async () => {
        const reqAgenda = {
          tenant_id: loginUserData.customer_id,
          session_code: session_code,
          session_started_at: UtilService.getTimeNumberFromString(currentSession['started_at']),
          session_completed_at: UtilService.getTimeNumberFromString(currentSession['ended_at']),
          from_epoch: UtilService.getTimeNumberFromString(currentSession['started_at']),
          to_epoch: UtilService.getTimeNumberFromString(currentSession['ended_at']),
          markers: reqMarkers,
          identifiers: identifiers
        };
        dispatch(await getAgendaImmersions(reqAgenda));
        dispatch(await getAgendaSafes(reqAgenda));
      };
      fetchAgendaData();
    }
  }, [markers, identifiers, session_code]);

  useEffect(() => {
    const timeOut = setTimeout(() => {
      if (!scrollRef.current) return;
      enableHorizontalScroll(scrollRef);
    }, 100)
    return () => clearTimeout(timeOut);
  }, [scrollRef, scrollAllPartRef, clip, loading])

  const pencilMarkerScroll = useCallback((scrollLeft: any) => {
    if (!pencilRef?.current) return;
    pencilRef.current.containerScrolled(Number(scrollLeft));
  }, [pencilRef]);

  const onContainerMouseMove = useCallback((event: any) => {
    if (!scrollRef.current) return
    const boundingRect = scrollRef.current.getBoundingClientRect();
    if (!pencilRef?.current) return;
    pencilRef.current.setMouseXPosition(Math.floor(Number(event.clientX)) - Math.floor(Number(boundingRect.left)));

    const commentContainer = document.getElementById("comment_container");
    if (!commentContainer) return;
    const commentBoundingRect = commentContainer.getBoundingClientRect();
    pencilRef.current.setMouseYPosition(Math.floor(Number(event.clientY)) - Math.floor(Number(commentBoundingRect.top)));

  }, [scrollRef]);

  useEffect(() => {
    const divHorScroll = document.getElementById("div_hor_scroll");
    if (!divHorScroll) return

    divHorScroll.onscroll = () => {
      const scrollLeft = divHorScroll.scrollLeft;
      pencilMarkerScroll(scrollLeft);
    }
  }, [pencilRef, clip, loading]);

  const onHandlePencil = useCallback(() => {
    if (pencil) {
      dispatch(updateSettings('pencil', !pencil));
      return;
    }
    dispatch(updateSettings('pencil', !pencil));
  }, [pencil]);

  useEffect(() => {
    const divHorScroll = document.getElementById("div_hor_scroll");
    if (!divHorScroll) return
    const scrollLeft = divHorScroll.scrollLeft;
    pencilMarkerScroll(scrollLeft);
  }, [onHandlePencil]);

  useEffect(() => {
    let request: any = {}
    request = history.location.state
    if (!!request) {
      dispatch(changeGraphType(0));
    }
  }, [history]);

  useEffect(() => {
    if (analyseEnd && analyseStart && graphLength) {
      dispatch(updateSettings('graphZoom', Math.sqrt(graphLength * 15 / paneWidth)));
    }
  }, [analyseEnd, analyseStart, width, cliped, clip, stimulus]);

  useEffect(() => {
    if (xScroll > 0 && scrollRef && !clip) {
      scrollRef.current.scrollLeft = xScroll + 5;
    }
  }, [graphZoom, xScroll]);

  useEffect(() => {
    if (stimulus) {
      if (blueLineWidth > paneWidth + scrollAllPartRef?.current?.scrollLeft) {
        if (scrollRef && scrollRef.current) {
          scrollRef.current.scrollLeft = scrollRef.current.scrollLeft + 300;
        }
      }
    }
  }, [videoPlayed]);

  const onZoom = (length: any, start: any) => {
    const zooming = Math.sqrt(length * 15 / paneWidth);
    const graphStart = cliped ? cliped.start_clip : (stimulus && session_type === 'ASYNC') ? 0 : analyseStart;

    if (zooming < 1) {
      dispatch(updateSettings('graphZoom', 1));
    } else {
      dispatch(updateSettings('graphZoom', zooming));
    }
    dispatch(updateSettings('xScroll', (start - graphStart) * 15 / Math.pow(zooming < 1 ? 1 : zooming, 2)))
  };

  const onScroll = (e: any) => {
    if (!scrollAllPartRef && !scrollAllPartRef.current && !scrollAllPartRef.current.scrollLeft) {
      return false;
    }
    if (!scrollAllPartRef.current) return false;
    scrollAllPartRef.current.scrollLeft = e.target.scrollLeft
  }

  const onArrowScroll = (e: any) => {
    if (!scrollRef && !scrollRef.current && !scrollRef.current.scrollLeft) {
      return false;
    }
    if (!scrollRef.current) return false;
    scrollRef.current.scrollLeft = scrollRef.current.scrollLeft + e
  }

  const onSetVideoPlayed = (e: any) => {
    if (!isDraggable && stimulus) return false;
    dispatch(updateSettings('videoPopIn', e))
    dispatch(updateSettings('clickedPillsTime', new Date().getTime()))
  }

  const onVideoResize = (e: any) => {

    let newWidth = videoWidth;
    let newHeight = videoHeight;

    setIsDraggable(false);

    if (Math.abs(e.movementX) > Math.abs(e.movementY)) {
      newWidth = videoWidth - e.movementX;
      newHeight = newWidth * 135 / 240;
    } else {
      newHeight = videoHeight + e.movementY;
      newWidth = newHeight * 240 / 135;
    }

    if (newWidth >= 200 && newHeight >= 112) {
      setVideoWidth(newWidth);
      setVideoHeight(newHeight);
    } else {
      setIsDraggable(true)
    }
  }

  const dragElement = <div style={{ zIndex: 1201, position: 'fixed' }}>
  <div className="videoDrag" style={{top: pdfLoading ? 0 : -20}}/>
  <ResizableBox
    className={pdfLoading ? classes.resizePDF : classes.resize}
    width={videoWidth}
    height={videoHeight}
    resizeHandles={['sw']}
    onResize={onVideoResize}
    onResizeStart={() => setIsDraggable(false)}
    onResizeStop={() => setTimeout(() => setIsDraggable(true), 500)}
  >
    <VideoSection
      width={videoWidth}
      height={videoHeight}
      video={stimulus?.play_url ? stimulus?.play_url : stimulus?.stimulus_link}
      videoStart={videoStart}
      videoEnd={videoEnd}
      img={(stimulus?.thumbnails && stimulus?.thumbnails.length > 0) ? stimulus?.thumbnails[0]?.url : ''}
      popOut={(e: any) => dispatch(updateSettings('xScroll', e * 15 / Math.pow(graphZoom, 2)))}
      position={'fixed'}
      sessionType={session_type}
      graphLength={graphLength}
    />
  </ResizableBox>

</div>

  return (
    <div className={classes.container}>
      {!clip &&
        <div className={classes.graphSection} style={{ height: UtilService.customHeightSafari(all, graphType) }}>
          {
            stimulus && 
            (session_status === 'COMPLETED' || session_type === 'ASYNC') && 
            videoAvailable 
            ? <Draggable
                bounds="parent"
                axis="both"
                disabled={!isDraggable}
                handle=".videoDrag"
                {...{children: dragElement}}
              />
              : <div className={classes.video} />
          }

          <div
            className={classes.pane}
            style={{ marginTop: (stimulus && videoAvailable) ? 56 : 35 }}
          >
            {graphs[0] && graphs[0].activeParticipant === 'All' && <div>
              <div className={classes.fixedScorePane} style={{ marginTop: (stimulus && videoAvailable) ? -110 : -41 }}>
                <div style={{
                  width: '100%',
                  height: (stimulus && videoAvailable) ? 110 : 40,
                  background: '#f0f0f0',
                }} />
                {graphType !== 1 && <ScorePane
                  sessionStatus={session_status}
                  immersionData={graphs[0].immersion}
                  participant={graphs[0].activeParticipant}
                  graphWidth={paneWidth}
                  marginBottom={0.1}
                />}
                {graphType === 1 && <ScorePane
                  sessionStatus={session_status}
                  participant={graphs[0].activeParticipant}
                  safetyOverall={graphs[0].overall_safety}
                  graphWidth={paneWidth}
                  marginBottom={-1}
                />}
              </div></div>}
            {graphs.map((data: any, index: number) => (
              <Fade key={index}>
                <div>
                  {graphType !== 1 && <ScorePane
                    sessionStatus={session_status}
                    immersionData={data.immersion}
                    participant={data.activeParticipant}
                    graphWidth={paneWidth}
                  />}
                  {graphType !== 0 && <ScorePane
                    sessionStatus={session_status}
                    participant={data.activeParticipant}
                    safetyOverall={data.overall_safety}
                    graphWidth={paneWidth}
                  />}
                </div>
              </Fade>
            ))}
          </div>

          <div
            className={clsx(classes.graphPane, 'custom_scroll')}
            id={"div_hor_scroll"}
            style={{ width: paneWidth, marginTop: (stimulus && videoAvailable) ? 0 : 50 }}
            onMouseMove={(e: any) => onContainerMouseMove(e)}
            ref={scrollRef}
            onScroll={onScroll}
          >
            <div
              className={classes.videoBlueLine}
              style={{ left: blueLineWidth }}
            >
              <div className={classes.time}>
                {blueLineLabel}
              </div>
            </div>
            
            <ReactCursorPosition
              hoverDelayInMs={250}
              hoverOffDelayInMs={150}
            >

              {pencil && graphs.length > 0 &&
                <PencilMarker
                  ref={pencilRef}
                  onAddNote={(x: any, y: any) => {
                    dispatch(updateSettings('addNoteX', x));
                    dispatch(updateSettings('addNoteY', y));
                    dispatch(updateSettings('pencil', false));
                  }}
                />}

              <div style={{ width: getMaxWidth, marginTop: all.length > 0 ? 35 : 60 }}>
                <BackgroundLines
                  getMaxWidth={getMaxWidth}
                  paneWidth={paneWidth}
                />

                {graphs[0] && graphs[0].activeParticipant === 'All' &&
                  <div
                    ref={scrollAllPartRef}
                    className={clsx('custom_scroll2', classes.fixedPane)}
                    style={{ width: paneWidth, height: graphType === 1 ? 232 : 260, marginTop: -40 }}
                  >
                    <BackgroundLines
                      getMaxWidth={getMaxWidth}
                      paneWidth={paneWidth}
                      allParticipants={true}
                    />

                    {graphType !== 1 && <div className={classes.customBottomline} style={{ background: UtilService.refactorGraphColor(graphType !== 1 ? (cliped ? cliped?.score?.immersion?.color_indicator : graphs[0].immersion.overall_index < 2 ? 'gray' : graphs[0].immersion.overall_color_indicator) : (graphs[0].overall_safety.safety_index > 1 ? graphs[0].overall_safety.color_indicator : 'gray')) }} />}
                    {graphType !== 1 && <div className={classes.customTopline} style={{ background: UtilService.refactorGraphColor(cliped ? cliped?.score?.immersion?.color_indicator : graphs[0].immersion.overall_index < 2 ? 'gray' : graphs[0].immersion.overall_color_indicator) }} />}
                    {graphType === 1 && <div className={classes.customTopline} style={{ background: UtilService.refactorGraphColor(cliped ? (cliped?.score?.safety?.safety_index > 1 ? cliped?.score?.safety?.color_indicator : 'gray') : graphs[0].overall_safety.safety_index > 1 ? graphs[0].overall_safety.color_indicator : 'gray') }} />}

                    <div
                      className={classes.customTopTitle}
                      style={{
                        color: UtilService.refactorGraphColor(graphType !== 1
                          ? (cliped ? cliped?.score?.immersion?.color_indicator : graphs[0].immersion.overall_color_indicator)
                          : (cliped ? (cliped?.score?.safety?.safety_index > 1 ? cliped?.score?.safety?.color_indicator : 'gray') : graphs[0].overall_safety.safety_index > 1 ? graphs[0].overall_safety.color_indicator : 'gray'))
                      }}
                    >
                      <p>All Participants </p>
                      <p>{cliped ? (graphType !== 1 ? cliped?.score?.immersion?.context_indicator : cliped?.score?.safety?.context_indicator) : (graphType !== 1 ? graphs[0].immersion.overall_context_indicator : graphs[0].overall_safety.context_indicator)}</p>
                    </div>
                    {graphType !== 1 && <CoreAnalysisLineBarGraph width={getMaxWidth} playPosition={blueLineWidth} height={paneWidth / 4.5} graphData={graphs[0].immersion} marginTop={41} marginBottom={42} popOut={onSetVideoPlayed} />}
                    {graphType !== 1 && <PillSection graphWidth={getMaxWidth} graphData={graphs[0]} videoPlayed={stimulus ? (videoPlayed * stimulus.stimulus_video_length) : 0} onZoom={(e: any, t: any) => onZoom(e, t)} popOut={onSetVideoPlayed} marginTop={48} />}
                    {graphType === 1 && <CoreAnalysisBarGraph graphData={graphs[0].safety} marginTop={1} marginBottom={0.1} />}
                  </div>
                }
                <div style={{ marginTop: (stimulus && videoAvailable) ? 0 : -22 }} >
                  {graphs.map((graphData: any, index: number) => (
                    <Fade key={index}>
                      <div>
                        {graphType !== 1 && <CoreAnalysisLineBarGraph width={getMaxWidth} playPosition={blueLineWidth} height={paneWidth / 4.5} graphData={graphData.immersion} popOut={onSetVideoPlayed} />}
                        {graphType !== 1 && <PillSection graphWidth={getMaxWidth} graphData={graphData} videoPlayed={stimulus ? (videoPlayed * stimulus.stimulus_video_length) : 0} onZoom={(e: any, t: any) => onZoom(e, t)} popOut={onSetVideoPlayed} />}
                        {graphType !== 0 && <CoreAnalysisBarGraph graphData={graphData.safety} />}
                      </div>
                    </Fade>
                  ))}
                </div>
                {session_type !== 'ASYNC' &&
                  <AgendaSection
                    graphWidth={getMaxWidth}
                    graphData={graphs[0]}
                    lineStep={15}
                    onZoom={(e: any, t: any) => onZoom(e, t)}
                  />
                }
                <MarkerSection addNoteX={addNoteX} addNoteY={addNoteY} pencil={pencil} lineStep={15} />
              </div>

            </ReactCursorPosition>

          </div>

        </div>
      }

      {clip &&
        <div className={classes.graphSection} style={{ marginTop: -47 }}>
          <ClipSection
            all={graphs}
            getMaxWidth={getMaxWidth}
            popOut={onSetVideoPlayed}
            handlePencil={() => {
              dispatch(updateSettings('pencil', !pencil));
            }}
          />
        </div>}

    </div>
  );
}

export default CompletedSection;

const useStyles = makeStyles(() => ({
  container: {
    flex: 1,
    width: '100%',
    display: 'flex',
    paddingTop: 49,
    marginLeft: -310,
  },
  graphSection: {
    flex: 1,
    flexDirection: 'column',
    display: 'flex',
    position: 'relative',
    alignItems: 'center',
    overflowX: 'hidden',
    scrollbarWidth: 'none',
    marginRight: -310,
  },
  graphPane: {
    position: 'relative',
    height: '100%',
  },
  customBottomline: {
    position: 'absolute',
    height: 4,
    width: '4000%',
    background: '#8192A2',
    bottom: 24
  },
  customTopline: {
    position: 'absolute',
    height: 4,
    width: '4000%',
    background: '#8192A2',
    top: 36
  },
  customTopTitle: {
    top: 33.5,
    position: 'absolute',
    color: 'red',
    fontSize: 23,
    fontFamily: 'Spartan-bold',
    left: 8,
    '& p': {
      position: 'fixed'
    },
    '& p:nth-child(2)': {
      marginTop: 46,
      fontSize: 15,
      fontFamily: 'Spartan-semibold',
      textTransform: 'uppercase',
      letterSpacing: 1.8
    }
  },
  reloadBtn: {
    color: '#017AEF',
    width: 70,
    height: 70
  },
  fixedPane: {
    position: 'fixed',
    zIndex: 5,
    background: '#F0F0F0',
    marginTop: 0,
  },
  fixedScorePane: {
    position: 'fixed',
    borderBottomLeftRadius: 20,
    borderBottomRightRadius: 20,
    overflow: 'hidden',
    scrollbarWidth: 'none',
    background: '#f0f0f0',
    zIndex: 5,
  },
  videoBlueLine: {
    width: 2,
    height: '100%',
    background: '#0075FF',
    position: 'absolute',
    left: 30,
    transition: '1.5s linear',
    zIndex: 12,
  },
  pane: {
    position: 'absolute',
    marginTop: 50,
  },
  video: {
    width: 1,
    height: 135,
    marginTop: -85,
    marginBottom: -100
  },
  time: {
    color: '#017AEF',
    fontSize: 17,
    fontFamily: 'Spartan-extrabold',
    background: '#F0F0F0',
    width: 38,
    height: 18,
    paddingTop: 2,
    marginLeft: -18,
    marginTop: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute'
  },
  resize: {
    width: 16,
    position: 'relative',
    marginBottom: -10,
    marginTop: -17
  },
  resizePDF: {
    width: 16,
    position: 'relative',
    marginBottom: -10,
    marginTop: 1
  }
}));